import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, Phone, ClipboardText, LinkSimple, UserFocus, IdentificationCard,
  CreditCard, Warning, ArrowsClockwise, UserGear, ClockCounterClockwise, Gear,
  SignOut, List, MagnifyingGlass, Bell, X, UserCircle, SlidersHorizontal, EnvelopeSimple, UsersThree, PaperPlaneTilt, Tray, ShieldCheck, ListChecks, Sparkle, WhatsappLogo,
  Newspaper,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, perm: null },
  { to: "/leads", label: "Leads", icon: Phone, perm: "leads:view" },
  { to: "/leads/quick-capture", label: "WhatsApp Capture", icon: WhatsappLogo, perm: "leads:manage" },
  { to: "/newsletter", label: "Newsletter", icon: Newspaper, perm: "leads:view" },
  { to: "/bookings", label: "Bookings", icon: ClipboardText, perm: "bookings:view" },
  { to: "/booking-links", label: "Booking Links", icon: LinkSimple, perm: "bookings:manage" },
  { to: "/allocation", label: "Allocation", icon: UserFocus, perm: "allocation:manage" },
  { to: "/maids", label: "Maid Database", icon: IdentificationCard, perm: "maids:view" },
  { to: "/payments", label: "Payments", icon: CreditCard, perm: "payments:view" },
  { to: "/complaints", label: "Complaints", icon: Warning, perm: "complaints:view" },
  { to: "/replacements", label: "Replacements", icon: ArrowsClockwise, perm: "replacements:view" },
  { to: "/customers", label: "Customers", icon: UsersThree, perm: "customers:view" },
  { to: "/communication/compose", label: "Compose Email", icon: PaperPlaneTilt, perm: "customers:view" },
  { to: "/communication/sent", label: "Sent Emails", icon: Tray, perm: "customers:view" },
  { to: "/communication/templates", label: "Email Templates", icon: EnvelopeSimple, perm: "*" },
  { to: "/communication/settings", label: "Email Settings", icon: ShieldCheck, perm: "*" },
  { to: "/admin/options", label: "Dropdown Options", icon: ListChecks, perm: "*" },
  { to: "/admin/ai", label: "AI Usage & Settings", icon: Sparkle, perm: "*" },
  { to: "/employees", label: "Employees", icon: UserGear, perm: "*" },
  { to: "/activity-logs", label: "Activity Logs", icon: ClockCounterClockwise, perm: null },
  { to: "/profile", label: "Profile", icon: UserCircle, perm: null },
  { to: "/my-settings", label: "Settings", icon: SlidersHorizontal, perm: null },
  { to: "/settings", label: "CRM Settings", icon: Gear, perm: "*" },
];

function Brand({ compact }) {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/logibyte-logo.png" alt="Logibyte Technologies" className="h-9 w-9 rounded-lg object-contain bg-white" />
      {!compact && (
        <div className="leading-tight">
          <div className="text-[13px] font-bold tracking-tight text-[#0F172A] font-head">LOGIBYTE TECHNOLOGIES</div>
          <div className="text-[10px] text-muted-foreground tracking-wide">Maidvy Operations CRM</div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [notes, setNotes] = useState({ notifications: [], unread: 0 });
  const [showNotes, setShowNotes] = useState(false);
  const nav = useNavigate();

  const items = NAV.filter((n) => n.perm === null || can(n.perm) || user?.role === "super_admin");

  const loadNotes = async () => {
    try { setNotes((await api.get("/notifications")).data); } catch (e) { console.error("Failed to load notifications:", e); }
  };
  useEffect(() => { loadNotes(); const t = setInterval(() => { if (!document.hidden) loadNotes(); }, 30000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (search.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { setResults((await api.get(`/search?q=${encodeURIComponent(search)}`)).data); } catch (e) { console.error("Search request failed:", e); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const go = (path) => { setSearch(""); setResults(null); nav(path); };
  const initials = (user?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className={`fixed z-40 inset-y-0 left-0 w-64 bg-white border-r border-border flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <Brand />
          <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-accent text-accent-foreground font-semibold shadow-[inset_2px_0_0_0_#1D4ED8]"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`
              }>
              <n.icon size={19} weight="regular" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 rounded-lg p-2 hover:bg-secondary transition-colors">
            <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground grid place-items-center text-xs font-bold">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.full_name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.role_label || user?.role}</div>
            </div>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" title="Sign out"
              className="text-muted-foreground hover:text-destructive transition-colors"><SignOut size={17} /></button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-border flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden text-foreground" onClick={() => setOpen(true)} data-testid="menu-toggle"><List size={22} /></button>
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="global-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search leads, bookings, maids…" className="pl-9 bg-secondary border-transparent h-9 focus-visible:ring-primary focus-visible:bg-white" />
            {results && (
              <div className="absolute mt-1 w-full bg-white border border-border rounded-xl shadow-xl max-h-80 overflow-y-auto z-50">
                {["leads", "bookings", "maids", "complaints"].map((k) =>
                  results[k]?.length ? (
                    <div key={k}>
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary">{k}</div>
                      {results[k].map((r) => (
                        <button key={r.id} onClick={() => go(`/${k}/${r.id}`)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex justify-between">
                          <span>{r.customer_name || r.full_name}</span>
                          <span className="text-xs brand-gold font-mono">{r.lead_id || r.booking_id || r.worker_id || r.complaint_id}</span>
                        </button>
                      ))}
                    </div>
                  ) : null
                )}
                {["leads", "bookings", "maids", "complaints"].every((k) => !results[k]?.length) && (
                  <div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>
                )}
              </div>
            )}
          </div>
          <div className="relative">
            <button data-testid="notifications-btn" onClick={() => setShowNotes(!showNotes)} className="relative text-muted-foreground hover:text-foreground p-2">
              <Bell size={20} />
              {notes.unread > 0 && <span className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 rounded-full bg-gold text-white text-[10px] flex items-center justify-center font-bold">{notes.unread}</span>}
            </button>
            {showNotes && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-border rounded-xl shadow-xl z-50">
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <span className="text-sm font-semibold">Notifications</span>
                  <button className="text-xs brand-gold" onClick={async () => { await api.post("/notifications/read-all"); loadNotes(); }}>Mark all read</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notes.notifications.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">No notifications</div>
                  ) : notes.notifications.slice(0, 20).map((n) => (
                    <div key={n.id} className={`px-3 py-2.5 border-b border-border/60 text-sm ${!n.read ? "bg-accent/50" : ""}`}>
                      <div className="font-medium text-xs brand-gold">{n.title}</div>
                      <div className="text-foreground/80">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1600px]">{children}</main>
      </div>
    </div>
  );
}
